import { Button, OdinIcon, OdinIconSize, SplitButton } from '@myosh/odin-components';
import { memo, useMemo } from 'react';
import type { BowtieDto as SaveBowtieDto } from '../../api/generated/bowtie-api';
import type { BowtieDto } from '../../api/generated/v4-api';
import { useAppPermissionsContext } from '../../context/app-permissions.context';
import { makeDataCy } from '../../helpers/util';
import useCreateBackendRecords from '../../hooks/use-create-backend-records-button-action';
import useDeleteDiagram from '../../hooks/use-delete-diagram-button-action';
import useSaveDiagram from '../../hooks/use-save-diagram-button-action';

// save icon
const SaveIcon = memo(function SaveIcon() {
  return <OdinIcon icon="Save" size={OdinIconSize.ExtraSmall} className="mr-2" />;
});
SaveIcon.displayName = 'SaveIcon';

interface CreateSplitButtonProps {
  bowtieDtoCreate?: BowtieDto;
  bowtieDtoSave?: SaveBowtieDto;
  mueLabel?: string;
  mueRecordId?: number;
}

const CreateSplitButton = ({ bowtieDtoCreate, bowtieDtoSave, mueLabel, mueRecordId }: CreateSplitButtonProps) => {
  const {
    loading: isSavingOrUpdating,
    label: saveOrUpdateButtonLabel,
    handleClick: handleSaveOrUpdateClick,
    diagramId,
  } = useSaveDiagram(bowtieDtoSave, mueLabel);

  // generate slug for fallback button selector
  const dataCyLabel = useMemo(() => makeDataCy(saveOrUpdateButtonLabel), [saveOrUpdateButtonLabel]);

  const { handleClick: handleCreateBackedRecordsClick, disabled: isCreateBackedRecordsDisabled } =
    useCreateBackendRecords(bowtieDtoCreate, mueLabel, mueRecordId);

  const { loading: isDeleting, handleClick: handleDeleteClick } = useDeleteDiagram(diagramId);

  const { canCreateBackendRecordsPermission } = useAppPermissionsContext();

  const { splitButtonItems, canRenderSplitButton } = useMemo(() => {
    const splitButtonItems = [
      {
        label: 'Create Backend Records',
        command: handleCreateBackedRecordsClick,
        visible: canCreateBackendRecordsPermission,
        icon: <OdinIcon icon="SquarePlus" size={OdinIconSize.ExtraSmall} className="mr-2" />,
      },
      {
        label: 'Delete',
        command: handleDeleteClick,
        visible: Boolean(diagramId),
        icon: <OdinIcon icon="Trash" size={OdinIconSize.ExtraSmall} className="mr-2" />,
      },
    ];

    const canRenderSplitButton = splitButtonItems.reduce((sum, item) => (item.visible === true ? sum + 1 : sum), 0) > 0;

    return { splitButtonItems, canRenderSplitButton };
  }, [handleCreateBackedRecordsClick, handleDeleteClick, diagramId]);

  const disabled = isSavingOrUpdating || isCreateBackedRecordsDisabled || isDeleting;

  return canRenderSplitButton ? (
    <SplitButton
      data-cy="split-button"
      label={saveOrUpdateButtonLabel}
      onClick={handleSaveOrUpdateClick}
      model={splitButtonItems}
      disabled={disabled}
      className="min-w-40"
      icon={<SaveIcon />}
    />
  ) : (
    <Button data-cy={`${dataCyLabel}-button`} onClick={handleSaveOrUpdateClick} disabled={disabled} type="primary">
      <SaveIcon />
      {saveOrUpdateButtonLabel}
    </Button>
  );
};

export default memo(CreateSplitButton);
